import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createExamD, fetchClasses } from "../api/client";
import type { ClassSummary } from "../api/types";
import { ArrowLeft, Upload, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

type FormState = {
  exam_name: string;
  subject: string;
  grade_level: string;
  test_date: string;
  max_points: string;
  feedback_language: string;
  class_id: string;
};

const today = new Date().toISOString().slice(0, 10);

const INITIAL: FormState = {
  exam_name: "",
  subject: "",
  grade_level: "",
  test_date: today,
  max_points: "100",
  feedback_language: "Turkish",
  class_id: "",
};

export default function ExamDCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const [form, setForm] = useState<FormState>(INITIAL);
  const [rubricFile, setRubricFile] = useState<File | null>(null);
  const [questionsFile, setQuestionsFile] = useState<File | null>(null);
  const [classes, setClasses] = useState<ClassSummary[]>([]);
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.exam_name.trim()) { setError(t("examDCreate.nameRequired")); return; }
    if (!form.test_date) { setError(t("examDCreate.dateRequired")); return; }

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    if (rubricFile) fd.append("rubric_pdf", rubricFile);
    if (questionsFile) fd.append("questions_pdf", questionsFile);

    setStatus("submitting");
    try {
      const { exam_id } = await createExamD(fd);
      navigate(`/exams-d/${exam_id}`);
    } catch (err: unknown) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t("examDCreate.createError"));
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-16 mb-24">
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
          {t("common.back")}
        </button>
        <div>
          <h2 className="section-title">{t("examDCreate.title")}</h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: 720 }}>
        {/* Exam info card */}
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t("examDCreate.infoCard")}</h3>

          <div className="flex flex-col gap-16">
            <div className="form-group">
              <label className="form-label">{t("examDCreate.nameLabel")}</label>
              <input
                type="text"
                className="form-input"
                placeholder={t("examDCreate.namePlaceholder")}
                required
                value={form.exam_name}
                onChange={(e) => update("exam_name", e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("examDCreate.subjectLabel")}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t("examDCreate.subjectPlaceholder")}
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("examDCreate.levelLabel")}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="9"
                  value={form.grade_level}
                  onChange={(e) => update("grade_level", e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("examDCreate.dateLabel")}</label>
                <input
                  type="date"
                  className="form-input"
                  required
                  value={form.test_date}
                  onChange={(e) => update("test_date", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("examDCreate.maxPointsLabel")}</label>
                <input
                  type="number"
                  className="form-input"
                  min={1}
                  value={form.max_points}
                  onChange={(e) => update("max_points", e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t("examDCreate.feedbackLangLabel")}</label>
                <select
                  className="form-select"
                  value={form.feedback_language}
                  onChange={(e) => update("feedback_language", e.target.value)}
                >
                  <option value="Turkish">{t("profile.feedbackLanguages.Turkish")}</option>
                  <option value="English">{t("profile.feedbackLanguages.English")}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("examDCreate.classLabel")}</label>
                <select
                  className="form-select"
                  value={form.class_id}
                  onChange={(e) => update("class_id", e.target.value)}
                >
                  <option value="">{t("examDCreate.selectClass")}</option>
                  {classes.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* PDF uploads card */}
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t("examDCreate.materialsCard")}</h3>
          <p className="text-sm text-muted mb-16">
            {t("examDCreate.materialsDesc")}
          </p>

          <div className="flex flex-col gap-16">
            <div className="form-group">
              <label className="form-label">{t("examDCreate.rubricLabel")}</label>
              <label className="upload-zone upload-zone-compact" style={{ cursor: "pointer" }}>
                <Upload size={16} className="upload-zone-icon" />
                <span className="upload-zone-label">
                  {rubricFile ? rubricFile.name : t("examDCreate.rubricSelectLabel")}
                </span>
                <input
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={(e) => setRubricFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">{t("examDCreate.questionPaperLabel")}</label>
              <label className="upload-zone upload-zone-compact" style={{ cursor: "pointer" }}>
                <FileText size={16} className="upload-zone-icon" />
                <span className="upload-zone-label">
                  {questionsFile ? questionsFile.name : t("examDCreate.rubricSelectLabel")}
                </span>
                <input
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={(e) => setQuestionsFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            className="mb-24"
            style={{
              padding: "12px 16px",
              borderRadius: "var(--radius-md)",
              background: "var(--color-danger-bg, #fef2f2)",
              color: "var(--color-danger)",
              fontSize: "var(--text-sm)",
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? t("examDCreate.submitting") : t("examDCreate.submit")}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate(-1)}
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
